import { MutableRefObject, useLayoutEffect, useMemo, useRef } from 'react';

import {
  GridApi,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';

import { FilterValues } from '../GeneratikTableFilterPanel.types';

export interface UseScrollResetOnQueryChangeParams {
  apiRef: MutableRefObject<GridApi>;
  /** Only server mode needs this — in client mode the grid never scrolled on data changes. */
  enabled: boolean;
  paginationModel?: GridPaginationModel;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
  filterValues?: FilterValues;
  searchValue?: string;
}

// In server mode rows change both when the user changes the query (page, sort,
// filters, search) and when a row action refreshes the same query. Only the
// former shows new content: scroll resets for it and survives the latter.
const useScrollResetOnQueryChange = ({
  apiRef,
  enabled,
  paginationModel,
  sortModel,
  filterModel,
  filterValues,
  searchValue,
}: UseScrollResetOnQueryChangeParams): void => {
  const querySignature = useMemo(
    () =>
      JSON.stringify([
        paginationModel?.page,
        paginationModel?.pageSize,
        sortModel,
        filterModel,
        filterValues,
        searchValue,
      ]),
    [paginationModel, sortModel, filterModel, filterValues, searchValue],
  );
  const previousSignature = useRef(querySignature);

  useLayoutEffect(() => {
    if (previousSignature.current === querySignature) return;
    previousSignature.current = querySignature;
    if (!enabled) return;

    const scroller = apiRef.current?.rootElementRef?.current?.querySelector<HTMLElement>(
      '.MuiDataGrid-virtualScroller',
    );
    if (scroller) scroller.scrollTop = 0;
  }, [querySignature, enabled, apiRef]);
};

export default useScrollResetOnQueryChange;
